import React from 'react';
import { useDispatch } from 'react-redux';
import { useUnits, DataGrid, useGridApiRef } from '@innovyze/stylovyze';
import { tableColumns } from './TabelColumns';
import { TableToolbar } from './TableToolbar';
import { useStyles } from './RangeDataGrid.styles';
import {
	type AttributeDataGridRow,
	type AttributeOption,
	type CountReply,
} from '@Types';
import {
	selectAttributeDataGrid,
	selectAttributeRangeUnitValues,
	selectAttributeRangeValues,
	selectIsCountLoading,
} from '@Selectors/Risk.selectors';
import {
	setAttributeDataGrid,
	setAttributeRangeUnitValues,
	setAttributeRangeValues,
	updateAttributeValuesDataGrid,
} from '@Actions/Risk.actions';
import { type AttributeValueChange } from '@Reducers/Risk.reducer';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

export const updateRangeAttributeCounts = (
	scoreRows: AttributeDataGridRow[],
	counts: CountReply[] | undefined,
	isMetric: boolean,
): AttributeDataGridRow[] => {
	if (counts === undefined || counts.length == 0) {
		const newScoreRows: AttributeDataGridRow[] = [];
		for (let i = 0; i < scoreRows.length; i++) {
			newScoreRows.push({
				...scoreRows[i],
				count: undefined,
				length: undefined,
				countPercent: undefined,
				lengthPercent: undefined,
			});
		}
		return newScoreRows;
	} else {
		const conversion = isMetric ? 1 : 0.06213712;
		const newScoreRows: AttributeDataGridRow[] = [];
		if (counts) {
			for (let i = 0; i < scoreRows.length; i++) {
				newScoreRows.push({
					...scoreRows[i],
					count: counts[i]?.count,
					length: Math.round(counts[i]?.length * conversion) / 100,
					countPercent:
						Math.round(counts[i]?.count_percent * 1000) / 10,
					lengthPercent:
						Math.round(counts[i]?.length_percent * 1000) / 10,
				});
			}
		} else {
			for (let i = 0; i < scoreRows.length; i++) {
				newScoreRows.push({
					...scoreRows[i],
					count: 0,
					length: 0,
					countPercent: 0,
					lengthPercent: 0,
				});
			}
		}

		return newScoreRows;
	}
};

interface RangeDataGridProps {
	countKey: string;
	option: AttributeOption;
	ranges: number;
	onSetRanges: (ranges: number) => void;
	onRangeValuesChanged: () => void;
	onRecalculateCount: () => void;
}

// This ensure we remove any extranious decimal places caused by javascript
const toFixedDP = (value: number, precision: number): number => {
	return +value.toFixed(precision);
};

const RangeDataGrid = ({
	countKey,
	option,
	ranges,
	onSetRanges,
	onRangeValuesChanged,
	onRecalculateCount,
}: RangeDataGridProps) => {
	const dispatch = useDispatch();
	const { getSystemUnit, getStandardisedValue, system } = useUnits();
	const classes = useStyles();

	const apiRef = useGridApiRef();

	const rows = selectAttributeDataGrid();
	const rangeValues = selectAttributeRangeValues();
	const rangeUnitValues = selectAttributeRangeUnitValues();

	const isCountLoading = selectIsCountLoading(countKey);

	const onRangeChange = (
		index: number,
		value: number,
		valueUnit?: number,
	) => {
		if (rangeValues[index] !== value) {
			onRangeValuesChanged();
			// Clear the existing counts so they have to manual request them
			if (rows[0].count !== undefined) {
				const newScoreRows = updateRangeAttributeCounts(
					rows,
					undefined,
					system === MEASUREMENT_SYSTEM.METRIC,
				);
				dispatch(setAttributeDataGrid(newScoreRows));
			}
			const decimalPt =
				option.config?.unitMetadata?.type === 'date'
					? 0
					: option.config?.unitMetadata?.precision !== undefined
						? option.config?.unitMetadata?.precision
						: 1;
			const minDiff = toFixedDP(1 / Math.pow(10, decimalPt), decimalPt);
			let workingValue = value;
			let newScore = [...rangeValues];
			// We need to work out if we need do this next bit in native or system units
			if (
				system === 'Imperial' &&
				rangeUnitValues &&
				option.config?.unitMetadata?.unit &&
				valueUnit != undefined
			) {
				workingValue = valueUnit;
				newScore = [...rangeUnitValues];
			}
			// Has the user decremented or incremented the value?
			if (workingValue < newScore[index]) {
				newScore[index] = workingValue;
				// Decrementing so we only need to worry about the items BEFORE this index and if it's the first item we dont need to do anything at all
				if (index !== 0) {
					// Have we overlapped the previous value?
					let nextValue = workingValue;
					for (let i = index - 1; i >= 0; i--) {
						if (newScore[i] >= nextValue) {
							nextValue = toFixedDP(
								nextValue - minDiff,
								decimalPt,
							);
							newScore[i] = nextValue;
						}
					}
				}
			} else {
				newScore[index] = workingValue;
				// Incremeting so we only need to worry about the items AFTER this index and if it's the last item we just need to update the last entry
				if (index == newScore.length - 2)
					newScore[index + 1] = workingValue;
				else {
					// Have we overlapped the next value?
					let nextValue = workingValue;
					for (let i = index + 1; i <= newScore.length - 2; i++) {
						if (newScore[i] <= nextValue) {
							nextValue = toFixedDP(
								nextValue + minDiff,
								decimalPt,
							);
							newScore[i] = nextValue;
						}
					}
				}
			}
			// Ensure the last two values are the same
			newScore[newScore.length - 1] = newScore[newScore.length - 2];
			// Are we running a different display unit?
			if (
				system === 'Imperial' &&
				rangeUnitValues &&
				option.config?.unitMetadata?.unit &&
				valueUnit != undefined
			) {
				const internalUnit = getSystemUnit(
					option.config?.unitMetadata?.unit,
				);
				let adjustment = 1;
				if (valueUnit !== undefined && value != valueUnit) {
					// mm -> in -> cm
					if (option.config?.unitMetadata?.unit === 'mm')
						adjustment = 10;
				}
				const nativeScores = newScore.map(score => {
					return (
						parseFloat(
							getStandardisedValue(`${score} ${internalUnit}`),
						) * adjustment
					);
				});
				dispatch(setAttributeRangeValues(nativeScores));
				dispatch(setAttributeRangeUnitValues(newScore));
				// Now switch back to the native scores for the next bit as thats in native units
				newScore = nativeScores;
			} else {
				dispatch(setAttributeRangeValues(newScore));
			}
			const newValues: AttributeValueChange[] = [];
			for (let i = 0; i < newScore.length; i++) {
				if (i == 0) {
					newValues.push({
						index: i,
						value: ` <= ${newScore[0]}`,
					});
				} else if (i < newScore.length - 1) {
					newValues.push({
						index: i,
						value: `${newScore[i - 1]} <= ${newScore[i]}`,
					});
				} else {
					newValues.push({
						index: i,
						value: `> ${newScore[i]}`,
					});
				}
			}
			dispatch(updateAttributeValuesDataGrid(newValues));
		}
	};

	return (
		<DataGrid
			apiRef={apiRef}
			disableColumnMenu
			disableColumnReorder
			disableRowSelectionOnClick
			rowSelection={false}
			className={classes.rangeDataGrid}
			skeletonRowCount={ranges + 3}
			columns={tableColumns(
				onRangeChange,
				countKey,
				option.config?.unitMetadata?.unit,
				option.config?.unitMetadata?.currency,
				option.config?.unitMetadata?.precision,
			)}
			rows={rows}
			slots={{
				toolbar: TableToolbar,
			}}
			slotProps={{
				toolbar: {
					name: option.value,
					isLoading: isCountLoading,
					onSetRanges: onSetRanges,
					onRecalculateCount: onRecalculateCount,
					ranges: ranges,
				},
			}}
			dataCy="score-table"
		/>
	);
};

export default RangeDataGrid;
